/**
 * The door to the `file-preview` chunk (2026-10-18, docs/PLAN-prest0n-edit-20261011.md C1): today's file viewer and
 * its floating windows.
 *
 * A viewer is drawn only while a file is open, so drawing one is asking for it: the chunk is fetched at once, and the
 * slot draws nothing until it has arrived. A file's tile asks for it earlier, at the browser's first idle moment
 * (preloadFilePreview, ChatAttachment.tsx), so the first press on a file opens it at once and a page with no file on
 * it never fetches it. The new design's viewer has its own doors (withPavilion.tsx); today's is their fallback.
 *
 * ON THE FIRST DOWNLOAD (the app-level layer, FloatingPreviewContext.tsx, imports it), so it holds the one dynamic
 * import and the slots that wait for it. The viewer is loaded here and nowhere else (scripts/check-chat-first-paint.mjs,
 * R4).
 */
import React, { useEffect } from 'react';
import { createOnDemandModule } from '../../utils/onDemandModule';
import { primePreviewVerdict } from '../../hooks/filePreview/previewVerdict';
// TYPE-only imports: erased at build, never an edge into the chunk.
import type * as FilePreviewNamespace from './filePreviewSurface';
import type { FilePreviewOverlayProps } from './FilePreviewOverlay';
import type { FloatingPreviewWindowProps } from './FloatingPreviewWindow';

export type { FilePreviewOverlayProps, FloatingPreviewWindowProps };
export type FilePreviewModule = typeof FilePreviewNamespace;

const preview = createOnDemandModule<FilePreviewModule>(
  () => import(/* webpackChunkName: "file-preview" */ './filePreviewSurface'),
);

/**
 * Today's viewer, once its file has arrived (fetched the moment it is drawn). The server's verdict for the file is
 * asked at the same moment (C1, previewVerdict.ts), so the header has it as soon as it can.
 */
export function FilePreviewOverlay(props: FilePreviewOverlayProps): React.ReactElement | null {
  const path = props.serialized?.storagePath ?? '';
  const thread = props.context?.threadId ?? '';
  useEffect(() => {
    void primePreviewVerdict(props.serialized, props.context);
    // The file and the thread it was opened from name the verdict.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, thread]);
  const loaded = preview.useModule(true, true);
  if (!loaded) return null;
  const { FilePreviewOverlay: Overlay } = loaded;
  return <Overlay {...props} />;
}

/** One of today's floating windows, once the viewer's file has arrived. */
export function FloatingPreviewWindow(props: FloatingPreviewWindowProps): React.ReactElement | null {
  const loaded = preview.useModule(true, true);
  if (!loaded) return null;
  const { FloatingPreviewWindow: Window } = loaded;
  return <Window {...props} />;
}

/** The longest the idle fetch may wait on a busy main thread. */
const PRELOAD_IDLE_TIMEOUT_MS = 4000;
let preloadAsked = false;

/**
 * Fetch today's viewer at the browser's first idle moment, once per page: a file's tile calls it when it is drawn where
 * today's design shows the viewer. Never throws: a failed fetch is forgotten, and opening a file tries again.
 */
export function preloadFilePreview(): void {
  if (preloadAsked || typeof window === 'undefined') return;
  preloadAsked = true;
  const fetchIt = () => { preview.load().catch(() => undefined); };
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fetchIt, { timeout: PRELOAD_IDLE_TIMEOUT_MS });
  else window.setTimeout(fetchIt, 1);
}

/** The viewer's file, fetched now if it has not arrived (tests draw it without waiting). */
export const loadFilePreview = preview.load;

/** Tests only: forget the download. */
export function resetFilePreviewForTests(): void {
  preview.resetForTests();
  preloadAsked = false;
}
